import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Endpoint } from '@/Config/api/endpoint';
import { QueryKey } from '@/Config/api/queryKey';
import { trackAndNavigate, pushGtmEvent } from '@/Config/libs/analytics';
import { SubscriptionApi } from '@/Domain/api/Subscription/Subscription.api';
import { SubscriptionPlansDto } from '@/Domain/api/Subscription/dto/SubscriptionPlans.dto';
import { GtmEventEnum } from '@/Domain/enum/GtmEvent.enum';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { SubscriptionIntervalEnum } from '@/Domain/enum/SubscriptionInterval.enum';
import { TierEnum } from '@/Domain/enum/Tier.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';

type Props = {
  successCallback: () => void;
  fromTier: TierEnum;
  toTier: TierEnum;
  interval: SubscriptionIntervalEnum;
  source?: string;
  /** Stripe Promotion Code to pre-apply at Checkout (onboarding discount flow). */
  promotionCode?: string;
  /**
   * Called when the subscription update mutation fails (e.g. Stripe outage,
   * 4xx/5xx from `/payments/handle-subscription/`). Callers that drive their
   * pending UI from local state (instead of `mutation.isPending`) need this
   * to unstick the UI on failure. Optional — call sites that already key off
   * `isPending` get the reset for free.
   */
  errorCallback?: (error: unknown) => void;
};

const TEN_SECONDS = 10000;
const CHECKOUT_CURRENCY = 'USD';
export const useUpdateSubscription = ({
  successCallback,
  fromTier,
  toTier,
  interval,
  source,
  promotionCode,
  errorCallback,
}: Props) => {
  const queryClient = useQueryClient();
  const { capture } = usePostHogAnalytics();

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const tierProperties = { from_tier: fromTier, to_tier: toTier, interval };

  const mutation = useMutation({
    mutationKey: [Endpoint.subscription.update],
    mutationFn: (priceId: string) => SubscriptionApi.update(priceId, source, promotionCode),
    onSuccess: (response, priceId) => {
      if (response?.session_url) {
        capture(PostHogEventEnum.SubscriptionCheckoutStarted, {
          price_id: priceId,
          source,
          // Lets us split discounted onboarding checkouts from full-price ones.
          discount_applied: !!promotionCode,
          ...tierProperties,
        });
        // Capture list-price revenue now, while the plans cache is warm — the post-Stripe return is a cold page load that races /v1/api/offer/ and usually loses.
        // Omit value when a promo is pre-applied (e.g. the 50%-off onboarding discount): the list price would overstate revenue and the real charged amount isn't known client-side, so honest omission beats a wrong number.
        const plans = queryClient.getQueryData<SubscriptionPlansDto>([Endpoint.subscription.list]);
        const priceInfo = plans?.subscriptions?.[toTier];
        const value = promotionCode
          ? undefined
          : interval === SubscriptionIntervalEnum.Annually
            ? priceInfo?.price_annually
            : priceInfo?.price_monthly;
        // Save tier info for subscription_started event after Stripe checkout return
        sessionStorage.setItem(
          '__qc_checkout_tier_info',
          JSON.stringify({
            price_id: priceId,
            source,
            ...tierProperties,
            ...(value != null && { value, currency: CHECKOUT_CURRENCY }),
          }),
        );
        trackAndNavigate(
          GtmEventEnum.SubscriptionCheckoutStarted,
          { price_id: priceId, ...tierProperties },
          () => window.location.replace(response.session_url),
        );
        return;
      }

      timeoutRef.current = setTimeout(() => {
        capture(PostHogEventEnum.PlanUpgraded, {
          price_id: priceId,
          ...tierProperties,
        });
        pushGtmEvent(GtmEventEnum.PlanUpgraded, {
          price_id: priceId,
          ...tierProperties,
        });
        // Refetch BOTH:
        // - [Endpoint.subscription.get] = `/v1/api/subscription/` — drives
        //   tier name / status / interval shown on the subscription page
        //   itself.
        // - ['subscription'] = `/v1/api/subscription/allowed_features` —
        //   drives `articles_limit`, `articles_used`, `can_add_article`,
        //   which feed the page-header bar, sidebar warning icon, and KB
        //   pill upgrade CTA. Without this second invalidation, an in-app
        //   plan change leaves the KB UX stuck on the OLD limit (e.g.
        //   100/100 capped after upgrading from trial → basic), even
        //   though the user just paid. The Stripe-checkout path (path 1)
        //   does a `window.location.replace` so the cache is rebuilt
        //   anyway; this one is for the in-app upgrade path only.
        // - [QueryKey.subscription.usage] = `/v1/api/.../message_usage` — drives the
        //   AI-credits ring/cards (messages_limit / messages_used). Without this the
        //   badge keeps the OLD plan's credit count after an in-app upgrade until a reload.
        Promise.all([
          queryClient.refetchQueries({ queryKey: [Endpoint.subscription.get] }),
          queryClient.refetchQueries({ queryKey: ['subscription'] }),
          queryClient.refetchQueries({ queryKey: [QueryKey.subscription.usage] }),
        ]).then(() => successCallback());
      }, TEN_SECONDS);
      return;
    },
    onError: (error) => {
      errorCallback?.(error);
    },
  });

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    [],
  );

  const handleUpdate = async (priceId: string) => {
    try {
      return await mutation.mutateAsync(priceId);
    } catch {
      // Error is already surfaced via `onError` → `errorCallback`. Swallow
      // the rejection here so call sites that fire-and-forget (`void
      // handleUpdate(...)`) don't trigger an unhandled-promise warning.
    }
  };

  return { handleUpdate, isPending: mutation.isPending };
};
